import { db } from "firebaseConfig";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@material-ui/core";
import { Close, NoteAlt } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { preventBubbling } from "utils/listings/helper";
import { FormEvent, useEffect, useState } from "react";
import { useModal } from "hooks/useModal";
import { getFullName } from "utils/Haim/utils";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import type { ChangeEvent } from "react";
import type { FavoriteListingsOfContact, RealEstateContact } from "../../../@types/sharedSchema";

type ListingCardNotesProps = {
  small?: boolean;
  listingId: string;
  contactData?: RealEstateContact;
  favoriteListingsOfContact: FavoriteListingsOfContact;
};

export function ListingCardNotes({
  small,
  listingId,
  contactData,
  favoriteListingsOfContact
}: ListingCardNotesProps): JSX.Element {
  const [listingNotesForm, setListingNotesForm] = useState("");
  const [loading, setLoading] = useState(false);

  const { t: translate } = useTranslation();

  const { open, openModal, closeModal } = useModal();

  const isCustomerListing = favoriteListingsOfContact.contactId.length;
  const listingNotes = favoriteListingsOfContact.additionalDetails?.[listingId]?.listingNotes;

  useEffect(() => {
    setListingNotesForm(listingNotes ?? "");
  }, [listingNotes]);

  const handleListingNotesSubmit = async (
    e: FormEvent<HTMLFormElement | HTMLInputElement | HTMLTextAreaElement>
  ): Promise<void> => {
    e.preventDefault();

    setLoading(true);

    const trimmedListingNotes = listingNotesForm.trim();

    if (trimmedListingNotes === listingNotes) {
      setLoading(false);
      setListingNotesForm(trimmedListingNotes);
      return;
    }

    const indexListingNotesFieldKey = getStringListingListingNotes(listingId);

    try {
      await db
        .collection("favorite-listings-of-contact")
        .doc(favoriteListingsOfContact.id)
        .update({
          [indexListingNotesFieldKey]: trimmedListingNotes
        });
    } catch {
      setListingNotesForm(listingNotes);
    } finally {
      setLoading(false);
    }
  };

  const handleListingNotesChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setListingNotesForm(value);
  };

  const buttonSize = small ? "small" : "medium";

  const preventBubbledCloseModal = preventBubbling(closeModal);

  const callerFullName = getFullName(contactData?.callerFirstName, contactData?.callerLastName);

  const validPhoneNumber = parsePhoneNumber(contactData?.id)?.formatNational() ?? contactData?.id;

  const formattedCustomerName = callerFullName || validPhoneNumber;

  const textLabel = isCustomerListing
    ? translate("listingsMatch.customerNotes", { customerName: formattedCustomerName })
    : translate("listingsMatch.listingNotes");

  return (
    <>
      <Dialog maxWidth="md" fullWidth open={open} onClose={preventBubbledCloseModal}>
        <Box onClick={preventBubbling()}>
          <DialogTitle sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4">{textLabel}</Typography>
              <IconButton onClick={preventBubbledCloseModal}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleListingNotesSubmit}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                value={listingNotesForm}
                disabled={loading}
                placeholder={textLabel}
                inputProps={{ sx: { resize: "none", overflow: "hidden" } }}
                InputProps={{
                  endAdornment: loading && <CircularProgress size={20} />
                }}
                onChange={handleListingNotesChange}
                onBlur={loading ? undefined : handleListingNotesSubmit}
              />
            </Box>
          </DialogContent>
        </Box>
      </Dialog>
      <IconButton
        color={listingNotes ? "primary" : "default"}
        size={buttonSize}
        onClick={preventBubbling(openModal)}
      >
        <NoteAlt />
      </IconButton>
    </>
  );
}

export function getStringListingListingNotes(listingId: string): string {
  return `additionalDetails.${listingId}.listingNotes`;
}
